import { BehavioralCollector } from "src/collector/BehavioralCollector";
import { DeviceCollector } from "src/collector/DeviceCollector";
import { NetworkCollector } from "src/collector/NetworkCollector";
import { ScreenActivityCollector } from "src/collector/ScreenActivityCollector";
import { SecurityCollector } from "src/collector/SecurityCollector";

import { DataCollector } from "src/core/contract/DataCollector";
import { EventCoordinator } from "src/core/engine/EventCoordinator";
import { TrackerEngine } from "src/core/engine/TrackerEngine";
import { TrackerConfiguration } from "src/core/model/TrackerConfiguration";
import { PeriodicDataScheduler } from "src/core/scheduler/PeriodicDataScheduler";
import { DefaultNetworkService } from "src/service/DefaultNetworkService";
import { EventService } from "src/service/EventService";
import { InMemoryEventStorage } from "src/service/InMemoryEventStorage";

import { DataPayload } from "./DataPayload";

/**
 * DI module that wires up all SDK components.
 * Mirrors Android TrackerModule + DaggerTrackerComponent.
 */
export class TrackerModule {
  private configuration: TrackerConfiguration;

  // Singleton instances
  private payload!: DataPayload;
  private deviceCollector!: DeviceCollector;
  private networkCollector!: NetworkCollector;
  private securityCollector!: SecurityCollector;
  private behavioralCollector!: BehavioralCollector;
  private screenActivityCollector!: ScreenActivityCollector;
  private networkService!: DefaultNetworkService;
  private eventStorage!: InMemoryEventStorage;
  private eventService!: EventService;
  private eventCoordinator!: EventCoordinator;
  private scheduler!: PeriodicDataScheduler;
  private engine!: TrackerEngine;

  constructor(configuration: TrackerConfiguration) {
    this.configuration = configuration;
    this.build();
  }

  private build(): void {
    // 1. Data payload (shared state)
    this.payload = new DataPayload();
    this.payload.setConfiguration(this.configuration);

    // 2. Collectors
    this.deviceCollector = new DeviceCollector();
    this.networkCollector = new NetworkCollector();
    this.securityCollector = new SecurityCollector();
    this.behavioralCollector = new BehavioralCollector();
    this.screenActivityCollector = new ScreenActivityCollector();

    const collectors: DataCollector[] = [
      this.deviceCollector,
      this.networkCollector,
      this.securityCollector,
      this.behavioralCollector,
      this.screenActivityCollector,
    ];

    // 3. Network service
    this.networkService = new DefaultNetworkService(this.configuration);

    // 4. Event storage & service
    this.eventStorage = new InMemoryEventStorage();
    this.eventService = new EventService(this.eventStorage, this.networkService, this.configuration);

    // 5. Event coordinator
    this.eventCoordinator = new EventCoordinator(
      this.eventService,
      collectors,
      this.payload,
      this.screenActivityCollector,
      this.securityCollector,
    );

    // 6. Periodic scheduler (security + behavioral rolling window reset)
    this.scheduler = new PeriodicDataScheduler(() => {
      this.securityCollector.resetRollingWindow();
      this.behavioralCollector.resetRollingWindow();
    }, 60_000);

    // 7. Engine
    this.engine = new TrackerEngine(
      this.eventService,
      this.eventCoordinator,
      this.networkService,
      this.payload,
      this.scheduler,
      this.deviceCollector,
      this.networkCollector,
      this.securityCollector,
      this.behavioralCollector,
      this.screenActivityCollector,
    );
  }

  getTrackerEngine(): TrackerEngine {
    return this.engine;
  }
}
